import { useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  type LeavesQuery,
  useLeavesQuery,
  useSetLeafFlagsMutation,
} from "@/graphql/generated";
import {
  type LeafField,
  type LeafFlags,
  type SaveEntry,
  type SaveMap,
  type SaveStatus,
  applySave,
  failedEntries,
  fieldStatus,
  forgetSaved,
  rowStatus,
  saveKey,
} from "@/lib/leaves/saveState";

export type { LeafField, LeafFlags, SaveEntry, SaveStatus };

type Leaf = LeavesQuery["leaves"][number];

const FIELD_NAMES: Record<LeafField, string> = {
  alwaysRequired: "Required",
  perStore: "Per store",
  label: "Label",
  inputType: "Input type",
  helpText: "Help text",
};

let map: SaveMap = {};
let announcement = "";
let failedCache: readonly SaveEntry[] = [];
const listeners = new Set<() => void>();

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

// Matches the tick fade in LeafSaveSlot.css, so a remount does not replay it.
const TICK_MS = 2300;

const forget = (entry: SaveEntry) => {
  const next = forgetSaved(map, entry);
  if (next === map) return;
  map = next;
  listeners.forEach((fn) => fn());
};

const record = (entry: SaveEntry, leafName: string, say: string) => {
  map = applySave(map, entry);
  announcement = `${say} ${FIELD_NAMES[entry.field]} for ${leafName}`;
  const next = failedEntries(map);
  const same =
    next.length === failedCache.length &&
    next.every((e, i) => e === failedCache[i]);
  if (!same) failedCache = next;
  listeners.forEach((fn) => fn());
  if (entry.status === "saved") setTimeout(() => forget(entry), TICK_MS);
};

const fieldOf = (flags: LeafFlags) => Object.keys(flags)[0] as LeafField;

export function useLeafSaveStatus(
  path: string,
  field?: LeafField,
): SaveStatus | null {
  return useSyncExternalStore(subscribe, () =>
    field ? fieldStatus(map, path, field) : rowStatus(map, path),
  );
}

export const useFailedLeafSaves = (): readonly SaveEntry[] =>
  useSyncExternalStore(subscribe, () => failedCache);

export const useLeafSaveAnnouncement = (): string =>
  useSyncExternalStore(subscribe, () => announcement);

export const useLeafSavedValue = (
  path: string,
  field: LeafField,
): string | boolean | null =>
  useSyncExternalStore(subscribe, () => {
    const e = map[saveKey(path, field)];
    return e && e.status !== "saved" ? e.value : null;
  });

export function useLeafFlagSaves() {
  const queryClient = useQueryClient();
  const key = useLeavesQuery.getKey();
  const mutation = useSetLeafFlagsMutation();

  const save = async (leaf: Leaf, flags: LeafFlags) => {
    const field = fieldOf(flags);
    const value = (flags as Record<string, string | boolean>)[field];
    const entry = { path: leaf.path, field, value };
    record({ ...entry, status: "saving", at: Date.now() }, leaf.leaf, "Saving");
    try {
      const { setLeafFlags: saved } = await mutation.mutateAsync({
        path: leaf.path,
        ...flags,
      });
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<LeavesQuery>(key, (old) =>
        old
          ? {
              ...old,
              leaves: old.leaves.map((l) =>
                l.path === saved.path ? { ...l, ...saved } : l,
              ),
            }
          : old,
      );
      record({ ...entry, status: "saved", at: Date.now() }, leaf.leaf, "Saved");
    } catch (error) {
      record(
        { ...entry, status: "failed", at: Date.now() },
        leaf.leaf,
        "Not saved:",
      );
      throw error;
    }
  };

  const retry = async (path: string) => {
    const leaf = queryClient
      .getQueryData<LeavesQuery>(key)
      ?.leaves.find((l) => l.path === path);
    if (!leaf) return;
    const failed = failedEntries(map).filter((e) => e.path === path);
    await Promise.allSettled(
      failed.map((e) => save(leaf, { [e.field]: e.value } as LeafFlags)),
    );
  };

  return { save, retry };
}
